import { Button, IconButton, Tooltip } from "@kaltura/ds-react-components";
import { fullWidthFlexItemStyles, translate, Uploader } from "@kaltura/mediaspace-shared-utils";
import { Controller, useFormContext } from "react-hook-form";
import { Container, StyledFormLabel } from "../index";
import { ReactNode, useRef, useState } from "react";
import { Remove24Icon, Upload24Icon } from "@kaltura/ds-react-icons";
import { RowItem } from "./RowItem";
import { Box } from "@kaltura/mediaspace-shared-styled";
import { FormFieldError } from "../../form/FormElementStyles";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import styled from "@emotion/styled";
import { RowContainer } from "./RowContainer";

export interface UploadFieldProps {
    /**
     * Icon for the uploaded file row
     */
    icon: ReactNode;
    /**
     * KS that could be used to upload the file to the API
     */
    ks: string;
    /**
     * Supported file types.
     */
    fileTypes: string;
}

/**
 * Name of the upload field in the form
 */
export const uploadFieldName = "uploadToken";

/**
 * Data stored in the upload field
 */
export interface UploadFieldData {
    id: string;
    fileName: string;
}

/**
 * Form field that uploads a file and shows an asset card when it's uploaded.
 */
export const UploadField = ({ icon, ks, fileTypes }: UploadFieldProps) => {
    const { control, setError, resetField } = useFormContext<{ [uploadFieldName]: UploadFieldData }>();

    const {
        application: { serviceUrl },
    } = useConfig();

    const [isUploading, setIsUploading] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleSelectFile = () => {
        fileInputRef.current?.click();
    };

    const handleUpload = (onChange: (data: UploadFieldData) => void) => {
        const fileInput = fileInputRef.current;
        const file = fileInput?.files?.[0];
        if (!file) {
            return;
        }

        // Clear the selection
        fileInput.value = "";

        resetField(uploadFieldName);
        setIsUploading(true);

        const uploader = new Uploader();
        uploader.upload({
            file,
            serviceUrl,
            ks,
            onUploadDone: (uploadTokenId, fileName) => {
                onChange({ id: uploadTokenId, fileName });
                setIsUploading(false);
            },
            onError: (error) => {
                console.error(error);
                setIsUploading(false);
                setError(
                    uploadFieldName,
                    {
                        type: "server",
                        message: translate("Upload failed, please try again."),
                    }
                );
            },
        });
    };

    return (
        <Container>
            <StyledFormLabel htmlFor={"upload"} text={translate("File")} required={true} fontWeightBold={true} />

            <Controller
                control={control}
                name={uploadFieldName}
                rules={{ required: true }}
                render={({ field: { ref, value, onChange, onBlur }, fieldState: { error } }) => (
                    <>
                        {!value && (
                            <div>
                                <Button
                                    ref={ref}
                                    startIcon={<Upload24Icon />}
                                    color={"secondary"}
                                    loading={isUploading}
                                    disabled={isUploading}
                                    onClick={handleSelectFile}
                                    onBlur={onBlur}
                                >
                                    {translate("Upload")}
                                </Button>
                            </div>
                        )}

                        {value && (
                            <RowContainer icon={icon}>
                                <Box sx={fullWidthFlexItemStyles}>
                                    <RowItem isMain={true} truncate={true}>
                                        {value.fileName}
                                    </RowItem>
                                </Box>
                                <Tooltip title={translate("Remove file")}>
                                    <IconButton color={"secondary"} onClick={() => resetField(uploadFieldName)}>
                                        <Remove24Icon />
                                    </IconButton>
                                </Tooltip>
                            </RowContainer>
                        )}

                        {error && <FormFieldError>{error.message || translate("Please upload a file")}</FormFieldError>}

                        <StyledHiddenInput
                            ref={fileInputRef}
                            id={"upload"}
                            type={"file"}
                            accept={fileTypes}
                            onChange={() => handleUpload(onChange)}
                        />
                    </>
                )}
            />
        </Container>
    );
};

const StyledHiddenInput = styled("input")({
    visibility: "hidden",
    display: "none",
});
